const API = '/api';

// Auth token persisted in localStorage; used as `Authorization: Bearer …` on every API call.
const TOKEN_KEY = 'auth:token';

export function getAuthToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token: string | null) {
  if (typeof localStorage === 'undefined') return;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

function authHeaders(): Record<string, string> {
  const t = getAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Wrapped fetch that injects auth + handles 401 globally.
const _origFetch = window.fetch.bind(window);
let _on401Handler: (() => void) | null = null;
export function setOn401(handler: () => void) {
  _on401Handler = handler;
}

window.fetch = async (input: RequestInfo | URL, init: RequestInit = {}) => {
  // Only inject auth for our API
  const url = typeof input === 'string' ? input : (input instanceof URL ? input.toString() : input.url);
  const isApi = url.startsWith('/api/') || url.startsWith(API);
  if (isApi) {
    const headers = new Headers(init.headers || {});
    const token = getAuthToken();
    if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    init = { ...init, headers };
  }
  const res = await _origFetch(input, init);
  if (isApi && res.status === 401) {
    setAuthToken(null);
    if (_on401Handler) _on401Handler();
  }
  return res;
};

// ─── Auth ───────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  fullName?: string | null;
  role: string;
  mode?: 'novice' | 'pro';
}

export interface AdminUserRow extends AuthUser {
  createdAt?: string;
  lastLoginAt?: string | null;
}

export async function patchMe(updates: { fullName?: string; mode?: 'novice' | 'pro' }): Promise<AuthUser> {
  const res = await fetch(`${API}/auth/me`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.message || 'Patch failed');
  }
  const j = await res.json();
  return j.user;
}

export async function listUsers(): Promise<AdminUserRow[]> {
  const res = await fetch(`${API}/auth/users`);
  if (!res.ok) throw new Error('listUsers failed');
  return res.json();
}

export async function patchUser(
  id: string,
  updates: { role?: 'admin' | 'user'; fullName?: string; mode?: 'novice' | 'pro' }
): Promise<AdminUserRow> {
  const res = await fetch(`${API}/auth/users/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.message || 'patchUser failed');
  }
  const j = await res.json();
  return j.user;
}

export interface AuthResponse {
  token: string;
  user: AuthUser;
}

export async function register(payload: {
  email: string;
  password: string;
  fullName?: string;
}): Promise<AuthResponse> {
  const res = await fetch(`${API}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  const res = await fetch(`${API}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function fetchMe(): Promise<AuthUser | null> {
  const res = await fetch(`${API}/auth/me`);
  if (res.status === 401) return null;
  if (!res.ok) throw new Error('me failed');
  const j = await res.json();
  return j.user;
}

export async function logoutApi(): Promise<void> {
  await fetch(`${API}/auth/logout`, { method: 'POST' });
}

export interface SessionSummary {
  id: string;
  title: string;
  state: string;
  updatedAt: string;
  createdAt: string;
}

export interface GeneratedFile {
  id?: string;
  url: string;
  filename: string;
  generatedAt?: string;
}

export interface TokenUsage {
  prompt_tokens?: number | null;
  completion_tokens?: number | null;
  total_tokens?: number | null;
}

export interface PlanItem {
  id: string;
  type: 'main' | 'attachment' | 'exhibit-list' | 'reminder';
  title: string;
  description?: string;
  template_id?: string;
  status: 'pending' | 'generated';
  order?: number;
  file_url?: string | null;
  filename?: string | null;
  generated_at?: string | null;
}

export interface TemplateField {
  key: string;
  label?: string;
  required?: boolean;
  type?: string;
}

export interface SessionDetail {
  id: string;
  title: string;
  state: string;
  category?: string | null;
  templateFields?: TemplateField[];
  chatHistory: Array<{ role: 'user' | 'assistant'; content: string; createdAt?: string }>;
  uploadedDocs: Array<{
    filename: string;
    name?: string;
    status?: string;
    id?: string;
    kind?: string;
    summary?: string;
    mimeType?: string;
    url?: string;
    useAsTemplate?: boolean;
  }>;
  generatedFiles: GeneratedFile[];
  collectedData?: Record<string, string>;
  lastUsage?: TokenUsage | null;
  documentPlan?: PlanItem[];
  updatedAt: string;
}

export interface ChatResponse {
  message: string;
  action?: string;
  title?: string;
  isResume?: boolean;
  state?: string;
  collectedData?: Record<string, string>;
  documentPlan?: PlanItem[];
}

export interface GenerateResponse {
  message: string;
  files: Array<GeneratedFile | string>;
  file?: GeneratedFile;
  documentPlan?: PlanItem[];
  nextDocumentId?: string | null;
  isResume?: boolean;
}

export async function getSessions(): Promise<SessionSummary[]> {
  const res = await fetch(`${API}/chat/sessions`);
  if (!res.ok) throw new Error('Failed to load sessions');
  return res.json();
}

export async function loadSession(sessionId: string): Promise<SessionDetail> {
  const res = await fetch(`${API}/chat/sessions/${sessionId}`);
  if (!res.ok) throw new Error('Failed to load session');
  return res.json();
}

export interface ChatStreamHandlers {
  onChunk?: (delta: string) => void;
  onUsage?: (usage: TokenUsage & { partial?: boolean }) => void;
  onFinal?: (final: ChatResponse & { usage?: TokenUsage | null; contextSize?: number | null }) => void;
  onMessageBreak?: () => void;
  onExtracted?: (collectedData: Record<string, string>) => void;
  onError?: (msg: string) => void;
  signal?: AbortSignal;
}

export async function streamChat(
  sessionId: string,
  message: string,
  handlers: ChatStreamHandlers
): Promise<void> {
  const res = await fetch(`${API}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({ sessionId, message }),
    signal: handlers.signal,
  });
  if (!res.ok || !res.body) {
    const txt = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${txt || res.statusText}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const events = buf.split('\n\n');
    buf = events.pop() ?? '';
    for (const evt of events) {
      const line = evt.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      const data = line.slice(5).trim();
      if (!data) continue;
      try {
        const j = JSON.parse(data);
        if (j.type === 'chunk' && typeof j.delta === 'string') handlers.onChunk?.(j.delta);
        else if (j.type === 'usage') handlers.onUsage?.(j.usage || {});
        else if (j.type === 'final') handlers.onFinal?.(j);
        else if (j.type === 'message_break') handlers.onMessageBreak?.();
        else if (j.type === 'extracted' && j.collectedData) handlers.onExtracted?.(j.collectedData);
        else if (j.type === 'error') handlers.onError?.(j.error || 'stream error');
      } catch {
        /* ignore */
      }
    }
  }
}

export interface ChatInfo {
  contextSize: number | null;
  model: string | null;
  fieldLabels?: Record<string, string>;
}

export async function getChatInfo(): Promise<ChatInfo> {
  const res = await fetch(`${API}/chat/info`);
  if (!res.ok) return { contextSize: null, model: null };
  return res.json();
}

// Legacy non-stream wrapper kept for compatibility (not used by new chat flow)
export async function sendMessage(sessionId: string, message: string): Promise<ChatResponse> {
  return new Promise((resolve, reject) => {
    let final: any = null;
    streamChat(sessionId, message, {
      onFinal: (f) => { final = f; },
      onError: (e) => reject(new Error(e)),
    })
      .then(() => resolve(final || { message: '', action: 'none' }))
      .catch(reject);
  });
}

export interface UploadedDocResult {
  id: string;
  filename: string;
  url: string;
  downloadUrl: string;
  summary: string;
  kind: string;
  mimeType?: string;
  size?: number;
}

export interface UploadResponse {
  uploadedDoc: UploadedDocResult;
  collectedData?: Record<string, string>;
  error?: string | null;
}

export async function uploadDocument(
  sessionId: string,
  file: File,
  onProgress?: (step: string, info?: Record<string, unknown>) => void
): Promise<UploadResponse> {
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1] || '');
    };
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(file);
  });

  const res = await fetch(`${API}/chat/upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({
      sessionId,
      imageBase64: base64,
      mimeType: file.type,
      filename: file.name,
    }),
  });
  if (!res.ok || !res.body) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Upload failed (${res.status})${txt ? ': ' + txt : ''}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let final: UploadResponse | null = null;
  let errorMsg: string | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const events = buf.split('\n\n');
    buf = events.pop() ?? '';
    for (const evt of events) {
      const line = evt.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      const data = line.slice(5).trim();
      if (!data) continue;
      try {
        const j = JSON.parse(data);
        if (j.type === 'progress') {
          onProgress?.(j.step as string, j);
        } else if (j.type === 'final') {
          final = {
            uploadedDoc: j.uploadedDoc,
            collectedData: j.collectedData,
            error: j.error,
          };
        } else if (j.type === 'error') {
          errorMsg = j.error || 'upload error';
        }
      } catch {
        /* ignore */
      }
    }
  }

  if (errorMsg) throw new Error(errorMsg);
  if (!final) throw new Error('Upload finished without final payload');
  return final;
}

export async function announceMessage(
  sessionId: string,
  content: string,
  role: 'assistant' | 'user' = 'assistant'
): Promise<{ collectedData?: Record<string, string> }> {
  const res = await fetch(`${API}/chat/sessions/${sessionId}/announce`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content, role }),
  });
  if (!res.ok) throw new Error('Announce failed');
  return res.json();
}

export async function generateDocument(sessionId: string): Promise<GenerateResponse> {
  const res = await fetch(`${API}/chat/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });
  if (!res.ok) throw new Error('Generation failed');
  return res.json();
}

export interface PreviewResponse {
  body: string;
  templateType?: string;
  templateId?: string;
}

export interface PreviewComment {
  id: string;
  text: string;
  comment: string;
}

export interface StreamHandlers {
  onMeta?: (meta: { templateType?: string; templateId?: string }) => void;
  onChunk?: (delta: string) => void;
  onDone?: (body: string) => void;
  onError?: (msg: string) => void;
  onToolUse?: (tool: string | null, server: string | null, fallback?: boolean) => void;
  signal?: AbortSignal;
}

async function consumeSSE(res: Response, handlers: StreamHandlers): Promise<string> {
  if (!res.ok || !res.body) {
    const txt = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${txt || res.statusText}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let full = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() ?? '';
    for (const evt of events) {
      const line = evt.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      const data = line.slice(5).trim();
      if (!data) continue;
      try {
        const json = JSON.parse(data);
        if (json.type === 'meta') handlers.onMeta?.(json);
        else if (json.type === 'tool_use') handlers.onToolUse?.(json.tool ?? null, json.server ?? null, json.fallback);
        else if (json.type === 'chunk' && typeof json.delta === 'string') {
          full += json.delta;
          handlers.onChunk?.(json.delta);
        } else if (json.type === 'done') {
          full = typeof json.body === 'string' ? json.body : full;
          handlers.onDone?.(full);
        } else if (json.type === 'error') {
          handlers.onError?.(json.error || 'stream error');
        }
      } catch {
        // ignore malformed events
      }
    }
  }
  return full;
}

export async function streamPreview(
  sessionId: string,
  handlers: StreamHandlers,
  documentId?: string | null,
  sourceDocId?: string | null,
): Promise<string> {
  const res = await fetch(`${API}/chat/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({
      sessionId,
      documentId: documentId || null,
      sourceDocId: sourceDocId || null,
    }),
    signal: handlers.signal,
  });
  return consumeSSE(res, handlers);
}

export async function setUploadedDocAsTemplate(
  sessionId: string,
  docId: string,
  useAsTemplate: boolean
): Promise<{ ok: boolean; docId: string; useAsTemplate: boolean }> {
  const res = await fetch(`${API}/chat/sessions/${sessionId}/uploaded-doc/${docId}/use-as-template`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ useAsTemplate }),
  });
  if (!res.ok) throw new Error('Не вдалося оновити документ');
  return res.json();
}

export async function deleteUploadedDoc(
  sessionId: string,
  docId: string
): Promise<{ ok: boolean; docId: string }> {
  const res = await fetch(`${API}/chat/sessions/${sessionId}/uploaded-doc/${docId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error('Не вдалося видалити документ');
  return res.json();
}

export async function streamRefine(
  sessionId: string,
  body: string,
  comments: PreviewComment[],
  handlers: StreamHandlers
): Promise<string> {
  const res = await fetch(`${API}/chat/refine`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({ sessionId, body, comments }),
    signal: handlers.signal,
  });
  return consumeSSE(res, handlers);
}

export async function finalizeDocument(
  sessionId: string,
  body: string,
  documentId?: string | null
): Promise<GenerateResponse> {
  const res = await fetch(`${API}/chat/finalize`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, body, documentId: documentId || null }),
  });
  if (!res.ok) throw new Error('Finalization failed');
  return res.json();
}

export async function setCaseCategoryApi(sessionId: string, category: string): Promise<void> {
  const res = await fetch(`${API}/chat/sessions/${sessionId}/category`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ category }),
  });
  if (!res.ok) throw new Error('Failed to set category');
}

export async function deleteSession(sessionId: string): Promise<void> {
  const res = await fetch(`${API}/chat/sessions/${sessionId}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Не вдалося видалити справу');
  }
}

export async function patchSessionData(
  sessionId: string,
  updates: Record<string, string | null>
): Promise<{ collectedData: Record<string, string> }> {
  const res = await fetch(`${API}/chat/sessions/${sessionId}/data`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error('Update failed');
  return res.json();
}

// ─── Templates (admin) ───────────────────────────────────────────────────────

export interface TemplateField {
  key: string;
  label: string;
  type?: string;
  description?: string;
}

export interface TemplateMeta {
  id: string;
  doc_type?: string;
  documentType?: string;
  description?: string;
  fields?: TemplateField[];
  requiredFields?: TemplateField[];
  filename?: string;
  originalName?: string;
  storageKey?: string;
  createdAt?: string;
}

export async function listTemplates(): Promise<TemplateMeta[]> {
  const res = await fetch(`${API}/templates`);
  if (!res.ok) throw new Error('Failed to load templates');
  return res.json();
}

export async function uploadTemplate(file: File): Promise<TemplateMeta> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API}/templates`, { method: 'POST', body: fd });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.details || err.error || 'Upload failed');
  }
  return res.json();
}

export async function patchTemplate(
  id: string,
  patch: { docType?: string; description?: string; fields?: TemplateField[] }
): Promise<TemplateMeta> {
  const res = await fetch(`${API}/templates/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error('Update failed');
  return res.json();
}

export async function reanalyzeTemplate(id: string): Promise<TemplateMeta> {
  const res = await fetch(`${API}/templates/${id}/reanalyze`, { method: 'POST' });
  if (!res.ok) throw new Error('Reanalyze failed');
  return res.json();
}

export async function deleteTemplate(id: string): Promise<void> {
  const res = await fetch(`${API}/templates/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Delete failed');
}
